import { esportaXLSX } from "../utils/esportaXLSX";
// src/pages/RegistroSopralluoghiPage.js
import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useOrg } from "../contexts/OrgContext";
import { AlertTriangle, Search, Printer } from "lucide-react";
import { useToast } from "../utils/Toast";
import { stampaSopralluogo } from "../utils/stampaSopralluogo";
import { dominioSito } from "../utils/fornitore";
import { useAuth } from "../contexts/AuthContext";
import { useCanSee } from "../hooks/useCanSee";
import CollegaSopralluogo from "../components/CollegaSopralluogo";
import { senzaCommessa, PERMESSO_SENZA_COMMESSA } from "../utils/sopralluoghi";

const esc = (v) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const dataIt = (iso) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("it-IT") : "—");

const STATI_SOP = {
  bozza:      { label: "Bozza",      bg: "#f3f4f6", col: "#6b7280" },
  completato: { label: "Completato", bg: "#d1fae5", col: "#065f46" },
  firmato:    { label: "Firmato",    bg: "#dbeafe", col: "#2563eb" },
};

export default function RegistroSopralluoghiPage({ onNavigate }) {
  const { effectiveOrgId } = useOrg();
  const [sopralluoghi, setSopralluoghi] = useState([]);
  const [progetti, setProgetti] = useState({});
  const [cerca, setCerca] = useState("");
  const [filtroStato, setFiltroStato] = useState("");
  const [filtroDataDa, setFiltroDataDa] = useState("");
  const [filtroDataA, setFiltroDataA] = useState("");
  const [soloAnomalie, setSoloAnomalie] = useState(false);
  const [soloSenzaCommessa, setSoloSenzaCommessa] = useState(false);
  const [daCollegare, setDaCollegare] = useState(null);
  const { isOrgAdmin, isPlatformOwner } = useAuth();
  const canSee = useCanSee();
  const puoSenzaCommessa = !!(isOrgAdmin || isPlatformOwner || canSee(PERMESSO_SENZA_COMMESSA));
  const [selezionato, setSelezionato] = useState(null);
  const [ordine, setOrdine] = useState("cliente"); // "cliente" | "cronologico"
  const [orgInfo, setOrgInfo] = useState(null);
  const { addToast } = useToast();

  useEffect(() => {
    if (!effectiveOrgId) return;
    getDoc(doc(db, "organizations", effectiveOrgId)).then(snap => { if (snap.exists()) setOrgInfo(snap.data()); }).catch(() => {});
  }, [effectiveOrgId]);

  useEffect(() => {
    if (!effectiveOrgId) return;
    return onSnapshot(
      query(collection(db, "sopralluoghi"), where("orgId", "==", effectiveOrgId)),
      snap => setSopralluoghi(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => b.data?.localeCompare(a.data || "") || 0))
    );
  }, [effectiveOrgId]);

  useEffect(() => {
    if (!effectiveOrgId) return undefined;
    return onSnapshot(query(collection(db, "projects"), where("organizationId", "==", effectiveOrgId)),
      snap => {
        const map = {};
        snap.docs.forEach(d => { map[d.id] = d.data(); });
        setProgetti(map);
      });
  }, [effectiveOrgId]);

  const filtrati = sopralluoghi.filter(s => {
    if (soloAnomalie && !(s.anomalie||[]).length) return false;
    if (soloSenzaCommessa && !senzaCommessa(s)) return false;
    if (filtroStato && s.stato !== filtroStato) return false;
    if (filtroDataDa && s.data < filtroDataDa) return false;
    if (filtroDataA && s.data > filtroDataA) return false;
    if (cerca) {
      const str = cerca.toLowerCase();
      return (s.numero||"").toLowerCase().includes(str) ||
        (s.clienteNome||"").toLowerCase().includes(str) ||
        (s.projectName||"").toLowerCase().includes(str) ||
        (s.luogo||"").toLowerCase().includes(str) ||
        (s.motivoSopralluogo||"").toLowerCase().includes(str) ||
        (s.personalePresente||[]).some(p => p.toLowerCase().includes(str)) ||
        (s.anomalie||[]).some(a => (a.descrizione||"").toLowerCase().includes(str));
    }
    return true;
  }).sort((a, b) => {
    if (ordine === "cliente") {
      const ca = (a.clienteNome || a.nomeCliente || "").toLowerCase();
      const cb = (b.clienteNome || b.nomeCliente || "").toLowerCase();
      if (ca !== cb) return ca.localeCompare(cb, "it");
      return b.data?.localeCompare(a.data || "") || 0;
    }
    return b.data?.localeCompare(a.data || "") || 0;
  });

  const nomeCommessa = (s) => s.projectName || progetti[s.projectId]?.name || "";
  const bloccata = () => addToast("Il browser ha bloccato la finestra: consenti le finestre pop-up per Nexiora", "error");
  const stampaVerbale = (s) => {
    const conCodice = { ...s, codiceCommessa: progetti[s.projectId]?.codiceCommessa || s.codiceCommessa || "" };
    if (!stampaSopralluogo(conCodice, orgInfo, nomeCommessa(s))) bloccata();
  };
  /** Elenco del registro (con i filtri attivi) da stampare o salvare in PDF. */
  const stampaElenco = () => {
    const win = window.open("", "_blank");
    if (!win) { bloccata(); return; }
    const filtri = [cerca && `ricerca «${cerca}»`, filtroStato && `stato ${STATI_SOP[filtroStato]?.label}`, filtroDataDa && `dal ${dataIt(filtroDataDa)}`, filtroDataA && `al ${dataIt(filtroDataA)}`, soloAnomalie && "solo con anomalie"].filter(Boolean).join(" · ");
    const righe = filtrati.map(s => {
      const anomalie = (s.anomalie || []).filter(a => a.descrizione).length;
      const codice = progetti[s.projectId]?.codiceCommessa || s.codiceCommessa || "";
      return `<tr><td class="n">${esc(s.numero || "—")}</td><td>${dataIt(s.data)}</td><td>${esc(s.clienteNome || s.nomeCliente || "—")}</td>
        <td>${senzaCommessa(s) ? "<i>Senza commessa</i>" : `${codice ? `<b>${esc(codice)}</b> ` : ""}${esc(nomeCommessa(s) || "—")}`}${s.rcNumero ? `<br/>da ${esc(s.rcNumero)}` : ""}</td><td>${esc(s.luogo || "—")}</td><td>${esc(s.motivoSopralluogo || "—")}</td>
        <td style="text-align:center">${anomalie || "—"}</td><td>${esc((s.personalePresente || []).join(", ") || "—")}</td><td>${esc((STATI_SOP[s.stato] || STATI_SOP.bozza).label)}</td></tr>`;
    }).join("");
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Registro sopralluoghi</title><style>
      *{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10px;color:#111;margin:0;padding:12mm}
      h1{font-size:15px;margin:0;color:#1d4ed8}.sub{color:#6b7280;margin:3px 0 10px}
      table{width:100%;border-collapse:collapse}th{background:#1e1b4b;color:#fff;text-align:left;padding:5px 6px;font-size:9px}
      td{border-bottom:1px solid #e5e7eb;padding:5px 6px;vertical-align:top}.n{font-family:monospace;font-weight:700;color:#1d4ed8;white-space:nowrap}
      tr{page-break-inside:avoid}.piede{margin-top:10px;color:#9ca3af;font-size:9px;display:flex;justify-content:space-between}
      .bt{position:fixed;top:14px;right:14px;padding:9px 18px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:13px;font-weight:700;cursor:pointer}
      @page{size:A4 landscape;margin:10mm}@media print{body{padding:0}.bt{display:none}}
    </style></head><body>
      <button class="bt" onclick="window.print()">🖨️ Stampa / Salva PDF</button>
      <h1>Registro sopralluoghi — ${esc(orgInfo?.nome || orgInfo?.name || "")}</h1>
      <div class="sub">${filtrati.length} sopralluogo/i${filtri ? ` · ${esc(filtri)}` : ""}</div>
      <table><thead><tr><th>ID</th><th>Data</th><th>Cliente</th><th>Commessa</th><th>Luogo</th><th>Motivo</th><th>Anomalie</th><th>Personale</th><th>Stato</th></tr></thead><tbody>${righe}</tbody></table>
      <div class="piede"><span>Generato da Nexiora — ${dominioSito()}</span><span>Stampato il ${new Date().toLocaleDateString("it-IT")}</span></div>
    </body></html>`);
    win.document.close();
  };

  const inp = { padding: "6px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12, fontFamily: "'Inter',sans-serif", outline: "none" };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", padding: "16px 24px", flexShrink: 0 }}>
        <div style={{ marginBottom: 12 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: "#1e1b4b", margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
            <AlertTriangle size={20} color="#2563eb"/> Registro Sopralluoghi
          </h2>
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <button onClick={()=>{
              const intestazione=["Numero","Data","Commessa","Cliente","Oggetto","N° Anomalie","Compilatore"];
              const righe=(sopralluoghi||[]).map(s=>[ s.numero||"—",s.data?new Date(s.data+"T00:00:00").toLocaleDateString("it-IT"):"—",s.projectName||"—",s.clienteNome||"—",s.oggetto||"—",s.anomalie?s.anomalie.length:0,s.compilatoreNome||"—"]);
              esportaXLSX([intestazione,...righe],`Registro_Sopralluoghi_${new Date().toLocaleDateString("it-IT").replace(/\//g,"-")}`,"Sopralluoghi");
            }} style={{display:"flex",alignItems:"center",gap:5,padding:"6px 12px",border:"1px solid #059669",borderRadius:7,background:"#f0fdf4",color:"#059669",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              📊 Excel
            </button>
            <button onClick={stampaElenco} disabled={filtrati.length === 0} title="L'elenco con i filtri attivi, da stampare o salvare in PDF"
              style={{display:"flex",alignItems:"center",gap:5,padding:"6px 12px",border:"1px solid #2563eb",borderRadius:7,background:"#eff6ff",color:"#2563eb",fontSize:12,fontWeight:700,cursor:filtrati.length?"pointer":"default",fontFamily:"'Inter',sans-serif",opacity:filtrati.length?1:.5}}>
              <Printer size={13}/> Stampa elenco / PDF
            </button>
          </div>
          <p style={{ fontSize: 12, color: "#9ca3af", margin: "2px 0 0" }}>{filtrati.length} sopralluogo/i</p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ position: "relative" }}>
            <Search size={13} style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }}/>
            <input value={cerca} onChange={e => setCerca(e.target.value)} placeholder="Cerca ID, cliente, commessa, luogo, anomalia..."
              style={{ ...inp, paddingLeft: 26, width: 260 }}/>
          </div>
          <button onClick={() => setSoloAnomalie(v=>!v)}
            style={{padding:"6px 12px",border:"1px solid "+(soloAnomalie?"#d97706":"#e5e7eb"),borderRadius:8,
              background:soloAnomalie?"#fef3c7":"#fff",color:soloAnomalie?"#92400e":"#374151",
              fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>
            ⚠️ Ha anomalie
          </button>
          <button onClick={() => setSoloSenzaCommessa(v=>!v)} aria-pressed={soloSenzaCommessa}
            style={{padding:"6px 12px",border:"1px solid "+(soloSenzaCommessa?"#d97706":"#e5e7eb"),borderRadius:8,
              background:soloSenzaCommessa?"#fffbeb":"#fff",color:soloSenzaCommessa?"#92400e":"#374151",
              fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>
            Senza commessa
          </button>
          <select value={filtroStato} onChange={e => setFiltroStato(e.target.value)} style={inp}>
            <option value="">Tutti gli stati</option>
            {Object.entries(STATI_SOP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <input type="date" value={filtroDataDa} onChange={e => setFiltroDataDa(e.target.value)} style={inp} title="Da data"/>
          <input type="date" value={filtroDataA} onChange={e => setFiltroDataA(e.target.value)} style={inp} title="A data"/>
          {(cerca||filtroStato||filtroDataDa||filtroDataA||soloAnomalie||soloSenzaCommessa) && (
            <button onClick={() => { setCerca(""); setFiltroStato(""); setFiltroDataDa(""); setFiltroDataA(""); setSoloAnomalie(false); setSoloSenzaCommessa(false); }}
              style={{ padding: "6px 12px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", fontSize: 12, cursor: "pointer" }}>
              × Reset
            </button>
          )}
          <div style={{ marginLeft: "auto", display: "flex", background: "#f3f4f6", borderRadius: 7, border: "1px solid #e5e7eb" }}>
            {[{id:"cliente",label:"Per cliente"},{id:"cronologico",label:"Cronologico"}].map(o => (
              <button key={o.id} onClick={() => setOrdine(o.id)}
                style={{ padding: "5px 12px", border: "none", borderRadius: 7,
                  background: ordine===o.id ? "#2563eb" : "transparent",
                  color: ordine===o.id ? "#fff" : "#6b7280",
                  fontSize: 12, fontWeight: ordine===o.id ? 700 : 400,
                  cursor: "pointer", fontFamily: "'Inter',sans-serif" }}>
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tabella */}
      <div style={{ flex: 1, overflow: "auto" }}>
        {filtrati.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: "#9ca3af" }}>
            <AlertTriangle size={48} style={{ opacity: 0.15, marginBottom: 12 }}/>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Nessun sopralluogo trovato</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>I sopralluoghi si creano nella commessa (scheda Sopralluogo) o, prima che la commessa esista, da «Sopralluoghi senza commessa»</div>
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead style={{ position: "sticky", top: 0, zIndex: 5 }}>
              <tr style={{ background: "#1e1b4b" }}>
                {["ID", "Data", "Cliente", "Commessa", "Luogo", "Motivo", "Anomalie", "Personale", "Stato", "Foto", "Verbale"].map(h => (
                  <th key={h} style={{ padding: "9px 12px", textAlign: "left", color: "#fff", fontSize: 10, fontWeight: 700, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtrati.map((s, idx) => {
                const stato = STATI_SOP[s.stato] || STATI_SOP.bozza;
                const anomalieConDescrizione = (s.anomalie || []).filter(a => a.descrizione);
                const prog = progetti[s.projectId];
                return (
                  <tr key={s.id} onClick={() => setSelezionato(selezionato?.id === s.id ? null : s)}
                    style={{ background: selezionato?.id===s.id ? "#eff6ff" : idx%2===0 ? "#fff" : "#fafafa",
                      borderBottom: "1px solid #f3f4f6", cursor: "pointer",
                      borderLeft: selezionato?.id===s.id ? "3px solid #2563eb" : "3px solid transparent" }}>
                    <td style={{ padding: "8px 12px", fontWeight: 800, color: "#1d4ed8", fontFamily: "monospace", whiteSpace: "nowrap" }}>
                      {s.numero || "—"}
                    </td>
                    <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                      {s.data ? new Date(s.data+"T00:00:00").toLocaleDateString("it-IT") : "—"}
                    </td>
                    <td style={{ padding: "8px 12px", fontWeight: 600 }}>{s.clienteNome || s.nomeCliente || "—"}</td>
                    <td style={{ padding: "8px 12px" }}>
                      {senzaCommessa(s) ? (
                        <span style={{ fontSize: 10.5, padding: "2px 8px", borderRadius: 10, background: "#fffbeb", color: "#92400e", border: "1px solid #fde68a", fontWeight: 700, whiteSpace: "nowrap" }}>Senza commessa</span>
                      ) : (<>
                        {(prog?.codiceCommessa || s.codiceCommessa) && <span style={{ color: "#7c3aed", fontFamily: "monospace", marginRight: 4 }}>{prog?.codiceCommessa || s.codiceCommessa}</span>}
                        {s.projectName || prog?.name || "—"}
                      </>)}
                      {s.rcNumero && <div style={{ fontSize: 10.5, color: "#6b7280", marginTop: 2 }}>da {s.rcNumero}</div>}
                    </td>
                    <td style={{ padding: "8px 12px", color: "#6b7280", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {s.luogo || "—"}
                    </td>
                    <td style={{ padding: "8px 12px", color: "#6b7280", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {s.motivoSopralluogo || "—"}
                    </td>
                    <td style={{ padding: "8px 12px", textAlign: "center" }}>
                      {anomalieConDescrizione.length > 0 ? (
                        <span style={{ fontSize: 10, padding: "1px 7px", borderRadius: 10, background: "#fef3c7", color: "#92400e", fontWeight: 700 }}>
                          ⚠️ {anomalieConDescrizione.length}
                        </span>
                      ) : <span style={{ color: "#d1d5db" }}>—</span>}
                    </td>
                    <td style={{ padding: "8px 12px", color: "#6b7280", whiteSpace: "nowrap" }}>
                      {(s.personalePresente || []).slice(0, 2).join(", ")}{(s.personalePresente||[]).length > 2 ? ` +${s.personalePresente.length-2}` : ""}
                    </td>
                    <td style={{ padding: "8px 12px" }}>
                      <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: stato.bg, color: stato.col, fontWeight: 700 }}>
                        {stato.label}
                      </span>
                    </td>
                    <td style={{ padding: "8px 12px", textAlign: "center" }}>
                      {(s.foto||[]).length > 0 ? <span style={{ fontSize: 11 }}>📷 {s.foto.length}</span> : "—"}
                    </td>
                    <td style={{ padding: "6px 12px" }}>
                      <button type="button" onClick={e => { e.stopPropagation(); stampaVerbale(s); }} title="Verbale da stampare o salvare in PDF" aria-label={`Verbale ${s.numero || ""} in PDF`}
                        style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, padding: "3px 8px", border: "1px solid #bfdbfe", borderRadius: 6, background: "#fff", color: "#2563eb", cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}>
                        <Printer size={11}/> PDF
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pannello dettaglio */}
      {selezionato && (
        <div style={{ borderTop: "2px solid #2563eb", background: "#eff6ff", padding: "16px 24px", maxHeight: 300, overflowY: "auto" }}>
          {/* spazio a destra: lì ci sono Nexi e il «+», che coprirebbero la × */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 8, marginBottom: 12, paddingRight: "min(130px, 22vw)" }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#1d4ed8" }}>Dettaglio {selezionato.numero}</div>
            {/* pulsanti accanto al titolo: a destra li coprirebbero Nexi e il «+» */}
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginLeft: 14, marginRight: "auto", flexWrap: "wrap" }}>
              <button type="button" onClick={() => stampaVerbale(selezionato)}
                style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, padding: "5px 12px", border: "none", borderRadius: 7, background: "#2563eb", color: "#fff", cursor: "pointer", fontWeight: 700 }}>
                <Printer size={12}/> Stampa / PDF
              </button>
              {onNavigate && selezionato.projectId && (
                <button type="button" onClick={() => onNavigate({ page: "project", projectId: selezionato.projectId, initialTab: "sopralluogo" })}
                  style={{ fontSize: 12, padding: "5px 12px", border: "1px solid #bfdbfe", borderRadius: 7, background: "#fff", color: "#2563eb", cursor: "pointer", fontWeight: 600 }}>
                  Apri nella commessa →
                </button>
              )}
              {puoSenzaCommessa && senzaCommessa(selezionato) && (
                <button type="button" onClick={() => setDaCollegare(selezionato)}
                  style={{ fontSize: 12, padding: "5px 12px", border: "1px solid #7c3aed", borderRadius: 7, background: "#f5f3ff", color: "#6d28d9", cursor: "pointer", fontWeight: 700 }}>
                  🔗 Collega a commessa
                </button>
              )}
            </div>
            <button onClick={() => setSelezionato(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#9ca3af" }}>×</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12, marginBottom: 12 }}>
            {[
              { l: "Data", v: selezionato.data ? new Date(selezionato.data+"T00:00:00").toLocaleDateString("it-IT",{weekday:"long",day:"2-digit",month:"long",year:"numeric"}) : "—" },
              { l: "Ora", v: selezionato.ora },
              { l: "Luogo", v: selezionato.luogo },
              { l: "Personale", v: (selezionato.personalePresente||[]).join(", ") },
              { l: "Altre persone", v: selezionato.altrePersone },
            ].map(f => f.v && (
              <div key={f.l}>
                <div style={{ fontSize: 10, color: "#2563eb", fontWeight: 700, textTransform: "uppercase", marginBottom: 2 }}>{f.l}</div>
                <div style={{ fontSize: 12, color: "#374151" }}>{f.v}</div>
              </div>
            ))}
          </div>
          {selezionato.motivoSopralluogo && <div style={{ marginBottom: 8 }}><div style={{ fontSize: 10, color: "#2563eb", fontWeight: 700, marginBottom: 2 }}>MOTIVO</div><div style={{ fontSize: 12, color: "#374151" }}>{selezionato.motivoSopralluogo}</div></div>}
          {selezionato.situazioneRilevata && <div style={{ marginBottom: 8 }}><div style={{ fontSize: 10, color: "#374151", fontWeight: 700, marginBottom: 2 }}>SITUAZIONE RILEVATA</div><div style={{ fontSize: 12, color: "#374151", whiteSpace: "pre-wrap" }}>{selezionato.situazioneRilevata}</div></div>}
          {(selezionato.anomalie||[]).filter(a=>a.descrizione).length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 10, color: "#d97706", fontWeight: 700, marginBottom: 6 }}>ANOMALIE RISCONTRATE</div>
              {(selezionato.anomalie||[]).filter(a=>a.descrizione).map((a,i) => (
                <div key={i} style={{ fontSize: 12, color: "#374151", marginBottom: 3, display: "flex", gap: 6, alignItems: "center" }}>
                  <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 8, background: a.gravita==="alta"?"#fee2e2":a.gravita==="media"?"#fef3c7":"#d1fae5", color: a.gravita==="alta"?"#991b1b":a.gravita==="media"?"#92400e":"#065f46", fontWeight: 700 }}>{a.gravita}</span>
                  {a.descrizione}
                </div>
              ))}
            </div>
          )}
          {selezionato.prescrizioni && <div style={{ marginBottom: 8 }}><div style={{ fontSize: 10, color: "#7c3aed", fontWeight: 700, marginBottom: 2 }}>PRESCRIZIONI</div><div style={{ fontSize: 12, color: "#374151", whiteSpace: "pre-wrap" }}>{selezionato.prescrizioni}</div></div>}
          {(selezionato.foto||[]).length > 0 && (
            <div>
              <div style={{ fontSize: 10, color: "#374151", fontWeight: 700, marginBottom: 6 }}>FOTO</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {(selezionato.foto||[]).map((f, i) => (
                  <img key={i} src={f.data} alt={f.nome||"foto"} style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 6, border: "1px solid #bfdbfe", cursor: "pointer" }}
                    onClick={() => window.open(f.data, "_blank")}/>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {daCollegare && (
        <CollegaSopralluogo sopralluogo={daCollegare} orgId={effectiveOrgId} onClose={() => setDaCollegare(null)}
          onCollegato={p => setSelezionato(sel => (sel?.id === daCollegare.id ? { ...sel, projectId: p.id, projectName: p.name || "", codiceCommessa: p.codiceCommessa || "" } : sel))} />
      )}
    </div>
  );
}
